import React, { useMemo, useState } from "react";
import dayjs from "lib/dayjs";
import ModalBackdrop from "components/ModalBackdrop";
import ExcelGrid from "components/ExcelGrid";
import { DateInput, TextInput } from "components/InputStyled";
import { Plus, Trash2, X } from "lucide-react";

function blankRow() {
  return { id: `custom-${Date.now()}-${Math.random()}`, date: "", yieldPct: "" };
}

export default function CustomCurveModal({ rows = [], curveDate = "", description = "", onSave, onClose }) {
  const initialRows = useMemo(
    () => (rows.length ? rows.map((row) => ({ ...row })) : [blankRow()]),
    [rows]
  );
  const [draft, setDraft] = useState(initialRows);
  const [draftDate, setDraftDate] = useState(curveDate);
  const [draftDescription, setDraftDescription] = useState(description);
  const [error, setError] = useState("");

  const columns = useMemo(
    () => [
      {
        key: "date",
        label: "Date",
        date: true,
        editor: "date",
        editable: true,
        className: "numeric",
      },
      {
        key: "yieldPct",
        label: "Yield (%)",
        numeric: true,
        editable: true,
        className: "text-right numeric",
        headerClassName: "text-right",
      },
      {
        key: "actions",
        label: "",
        width: "w-20 min-w-20",
        render: (_row, _rows, rowIndex) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                setDraft((current) => [
                  ...current.slice(0, rowIndex + 1),
                  blankRow(),
                  ...current.slice(rowIndex + 1),
                ])
              }
              className="text-green-600 hover:text-green-800"
              title="Add row below"
              aria-label={`Add row below row ${rowIndex + 1}`}
            >
              <Plus size={16} />
            </button>
            <button
              type="button"
              onClick={() =>
                setDraft((current) => {
                  const remaining = current.filter((_, index) => index !== rowIndex);
                  return remaining.length ? remaining : [blankRow()];
                })
              }
              className="text-red-500 hover:text-red-700"
              title="Delete row"
              aria-label={`Delete row ${rowIndex + 1}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ),
        copyValue: () => "",
      },
    ],
    []
  );

  const save = () => {
    const populated = draft.filter(
      (row) => String(row.date || "").trim() || String(row.yieldPct ?? "").trim()
    );
    const errors = [];
    const dates = new Set();
    const referenceDate = dayjs(draftDate);
    if (draftDate && !referenceDate.isValid()) errors.push("Enter a valid as-of date.");
    populated.forEach((row, index) => {
      const date = dayjs(row.date);
      const yieldPct = Number(row.yieldPct);
      if (!row.date || !date.isValid()) errors.push(`Row ${index + 1} requires a valid date.`);
      else if (dates.has(row.date)) errors.push(`Duplicate curve date ${row.date}.`);
      else dates.add(row.date);
      if (String(row.yieldPct ?? "").trim() === "" || !Number.isFinite(yieldPct) || yieldPct <= -200) {
        errors.push(`Row ${index + 1} requires a numeric yield greater than -200%.`);
      }
    });
    if (populated.length < 2) errors.push("Enter at least two dated yield points.");
    if (errors.length) {
      setError(errors.join(" "));
      return;
    }
    onSave?.({
      date: draftDate,
      description: draftDescription.trim(),
      rows: populated
        .map((row) => ({ ...row, yieldPct: Number(row.yieldPct) }))
        .sort((a, b) => a.date.localeCompare(b.date)),
    });
    onClose?.();
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="custom-curve-title"
        className="w-full max-w-xl max-h-[90vh] overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h2 id="custom-curve-title" className="text-lg font-semibold text-app">User-Defined Curve</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-muted hover:text-app" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 overflow-auto max-h-[65vh]">
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DateInput
              label="As of Date (Optional)"
              value={draftDate}
              onChange={(value) => {
                setDraftDate(value);
                setError("");
              }}
              className="w-full"
            />
            <TextInput
              label="Description"
              value={draftDescription}
              onChange={setDraftDescription}
              placeholder="Optional short description"
              maxLength={160}
              className="w-full"
            />
          </div>
          <ExcelGrid
            columns={columns}
            rows={draft}
            onChange={(nextRows) => {
              setDraft(
                Array.isArray(nextRows) && nextRows.length
                  ? nextRows
                  : [blankRow()]
              );
              setError("");
            }}
            showCopyButton={true}
            allowPasteAppendRows={true}
            makePasteRow={(partial = {}) => ({
              ...blankRow(),
              date: partial.date ?? "",
              yieldPct: partial.yieldPct ?? "",
            })}
            viewportConstrained
          />
          {error ? <p className="mt-3 text-sm text-red-500">{error}</p> : null}
        </div>

        <div className="flex justify-end gap-3 border-t border-border px-5 py-4">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save}>Save Curve</button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
